import React, {Component, MouseEvent, ReactNode} from 'react';
import {Product, ProductCategory, ProductSKU} from 'services/entities/products';
import {Booking, BookingProduct, DraftBooking} from 'services/entities/booking';
import {
  getBookingProductQuantityMultiplier,
  loadBookingFinalPaymentDate,
  loadBookingPrice,
  loadBookingProductPrice,
  loadBookingRentalPrice,
  loadBookingTimeRentalPrice,
  loadDiscountAmount, loadFlexiTicketPrice
} from 'services/bookings';
import {CurrencyAmount} from 'util/currency';
import {Space} from 'services/entities/spaces';
import {loadSpace} from 'services/spaces';
import {loadSpaceProducts} from 'services/products';
import './index.scss';
import {DateTime} from 'util/time';
import {applyTaxes} from 'util/pricing';
import {TaxType} from 'services/entities/taxes';
import {loadTaxes} from 'services/pricing';
import {Discount} from 'services/entities/discounts';
import {loadDiscounts} from 'services/discounts';
import {canModifyBookingDiscounts, getBookingHash} from 'util/booking';
import AddDiscountForm from 'pages/bookings/edit/components/AddDiscountForm';
import {isDraftBooking} from 'services/bookings/drafts';

interface BookingSummaryProps {
  priceChildren?: ReactNode;
  booking: DraftBooking|Booking;
  onDiscountsChanged?: ()=>void;
  onDiscountRemoved?: (discount: Discount)=>void;
}

interface PricedDiscount {
  discount: Discount;
  amount: CurrencyAmount|null;
}

interface BookingSummaryState {
  products: Product[]|null;
  bookingSpace: Space|null;
  bookingPrice: CurrencyAmount|null;
  bookingRentalPrices: (CurrencyAmount|null)[]|null;
  bookingFlexiTicketPrices: (CurrencyAmount|null)[]|null;
  bookingProductPrices: {[index:string]: CurrencyAmount}|null,
  finalPaymentDate: DateTime|null;
  taxes: TaxType[]|null;
  discounts: PricedDiscount[]|null;
  bookingHash: string|null;
}

export default class BookingSummary extends Component<BookingSummaryProps, BookingSummaryState> {
  state: BookingSummaryState = {
    bookingPrice: null,
    bookingSpace: null,
    bookingRentalPrices: null,
    bookingFlexiTicketPrices: null,
    bookingProductPrices: null,
    products: null,
    taxes: null,
    finalPaymentDate: null,
    discounts: null,
    bookingHash: null
  };

  componentDidMount(): void {
    this.loadBookingDetails(this.props.booking);
  }

  componentDidUpdate(prevProps: Readonly<BookingSummaryProps>, prevState: Readonly<BookingSummaryState>, snapshot?: any) {
    if(prevProps.booking !== this.props.booking || getBookingHash(this.props.booking) !== this.state.bookingHash) {
      this.loadBookingDetails(this.props.booking);
    }
  }

  loadBookingDetails(booking: DraftBooking|Booking) {
    this.setState({bookingHash: getBookingHash(booking)});
    loadSpace(booking.space).then(
      space => this.setState({bookingSpace: space})
    );
    loadBookingPrice(booking, true, true).then(
      price => {
        this.setState({bookingPrice: price});
        if(price && price.currency) {
          loadTaxes(price.currency).then(
            taxes => {
              this.setState({taxes: taxes}, () => {
                if(this.state.products) {
                  this.loadProductPrices(booking, this.state.products!, taxes);
                }
              });
            }
          );
        }
      }
    );
    if('times' in booking) {
      Promise.all(booking.times.map(time => loadBookingTimeRentalPrice(booking, time.startTime, time.endTime, true))).then(
        prices => this.setState({bookingRentalPrices: prices})
      );
      Promise.all(booking.times.map(time => loadFlexiTicketPrice(booking, time.startTime, time.endTime, true))).then(
        prices => this.setState({bookingFlexiTicketPrices: prices})
      );
    } else {
      loadBookingRentalPrice(booking, true).then(
        price => this.setState({bookingRentalPrices: [price]})
      );
    }
    loadSpaceProducts(booking.space).then(
      products => {
        this.setState({products: products}, () => {
          if(this.state.taxes) {
            this.loadProductPrices(booking, products, this.state.taxes!);
          }
        });
      }
    );
    loadBookingFinalPaymentDate(booking).then(
      date => this.setState({finalPaymentDate: date})
    );
    this.loadDiscounts(booking).then(
      discounts => this.setState({discounts: discounts})
    );
  }

  static formatBookingTime(start: DateTime, end:DateTime): string {
    return start.format('dddd D MMMM h:mmA') + " - " + end.format("h:mmA");
  }

  async loadProductPrices(booking: DraftBooking|Booking, products: Product[], taxes: TaxType[]) {
    const productPrices: {[index: string]: CurrencyAmount} = {};
    for(const bookingProduct of booking.products) {
      const product = products.find(pr => pr.sku === bookingProduct.sku);
      if(!product) {
        console.warn("Unable to find product: " + bookingProduct.sku);
        continue;
      }
      const price = await loadBookingProductPrice(booking, product);
      if(price) {
        productPrices[product.sku] = applyTaxes(price, taxes);
      }
    }
    this.setState({bookingProductPrices: productPrices});
  }

  async loadDiscounts(booking: DraftBooking|Booking): Promise<PricedDiscount[]> {
    if(booking.discounts.length === 0) {
      return [];
    }
    let discounts: Discount[] = await loadDiscounts(booking.discounts);
    return await Promise.all(discounts.map(d => {
      return new Promise<PricedDiscount>((resolve, reject) => {
        loadDiscountAmount(booking, d, true).then(
          amount => resolve({ discount: d, amount: amount }),
          reject
        );
      });
    }));
  }

  onDiscountsChanged = () => {
    if(this.props.onDiscountsChanged) {
      this.props.onDiscountsChanged();
    }
  };

  onRemoveDiscountClick = (ev: MouseEvent, discount: Discount) => {
    ev.preventDefault();
    if(this.props.onDiscountRemoved) {
      this.props.onDiscountRemoved(discount);
    }
  };

  renderCategoryProducts(category: ProductCategory) {
    const products: Product[]|null = this.state.products;
    if(!products) {
      return;
    }
    const productPrices = this.state.bookingProductPrices;
    if(!productPrices) {
      return;
    }
    const booking: DraftBooking|Booking = this.props.booking;
    let renderedProducts = [];
    for(const pr of products!.filter(pr => pr.category === category)) {
      const bookingProduct = booking!.products.find(bp => bp.sku === pr.sku);
      if(bookingProduct && bookingProduct.quantity > 0) {
        const price: CurrencyAmount|null = productPrices ? productPrices[pr.sku] : null;
        const note = BookingSummary.getProductNote(pr, booking);
        renderedProducts.push(<div className="product" key={renderedProducts.length - 1}>
          <span className="name">{ BookingSummary.getProductDisplayName(pr, bookingProduct, booking) }</span>
          <span className="price">{ price && price!.amount > 0 ? price!.format('$0') : 'Free' }</span>
          { note && <span className="note">{ note }</span> }
        </div>);
      }
    }
    if(category === ProductCategory.FlexiTicket && 'times' in booking) {
      const flexiTicketPrices = this.state.bookingFlexiTicketPrices;
      const product = products!.find(pr => pr.sku === ProductSKU.FlexiTicket);
      for(let k = 0; k < booking.times.length; k++) {
        const t = booking.times[k];
        if(t.includeFlexiTicket) {
          const price: CurrencyAmount|null = flexiTicketPrices ? flexiTicketPrices[k] : null;
          renderedProducts.push(<div className="product" key={renderedProducts.length - 1}>
            <span className="name">{ product!.name } ({ BookingSummary.formatBookingTime(t.startTime, t.endTime) })</span>
            <span className="price">{ price ? price!.format('$0') : '' }</span>
          </div>);
        }
      }
    }
    return renderedProducts;
  }

  static getProductDisplayName(product: Product, bookingProduct: BookingProduct, booking: DraftBooking|Booking): string {
    switch(product.sku) {
      case ProductSKU.Staff:
        const hours = getBookingProductQuantityMultiplier(booking, product.sku);
        return `${bookingProduct.quantity} Staff x ${hours} Hours`;
      case ProductSKU.StorageBoxes:
        return `Storage x ${bookingProduct.quantity}`;
      case ProductSKU.FlexiTicket:
        return product.name;
      default:
        return `${product.name} x ${bookingProduct.quantity}`;
    }
  }

  static getProductNote(product: Product, booking: DraftBooking|Booking): string|null {
    switch(product.sku) {
      case ProductSKU.StorageBoxes:
        let days = getBookingProductQuantityMultiplier(booking, product.sku);
        return days > 1 ? `${days} days` : '1 day';
      default:
        return null;
    }
  }

  render() {
    const booking: DraftBooking|Booking = this.props.booking;
    const price: CurrencyAmount|null = this.state.bookingPrice;
    const rentalPrices: (CurrencyAmount|null)[]|null = this.state.bookingRentalPrices;
    const space: Space|null = this.state.bookingSpace;
    const finalPaymentDate: DateTime|null = this.state.finalPaymentDate;
    const discounts: PricedDiscount[]|null = this.state.discounts;
    return <div className="booking-summary">
      <div className="rental">
        <span className="space label">Where</span>
        <span className="space value">{ space ? space!.name : '' }</span>
        <span className="time label">When</span>
        { 'times' in booking && booking.times.map((t, i) => {
          return <span className="time value" key={i}>{ BookingSummary.formatBookingTime(t.startTime, t.endTime) }</span>
        }) }
        { 'startTime' in booking && 'endTime' in booking && <span className="time value">
          { BookingSummary.formatBookingTime(booking.startTime, booking.endTime) }</span> }
        <span className="price label" />
        { rentalPrices && rentalPrices!.map((pr, i) => {
            return <span className="price value" key={i}>{ pr ? pr!.format('$0') : '' }</span>
          }) }
      </div>
      <div className="add-ons">
        <h2>Add-Ons</h2>
        { this.renderCategoryProducts(ProductCategory.Staff) }
        { this.renderCategoryProducts(ProductCategory.Furniture) }
        { this.renderCategoryProducts(ProductCategory.Storage) }
        { this.renderCategoryProducts(ProductCategory.FlexiTicket) }
      </div>
      { discounts && discounts!.length > 0 &&
        <div className="discounts">
          <h2>Discount</h2>
          { discounts!.map((d, i) => <div className="discount" key={i}>
            <span className="description">{ d.discount.description }</span>
            { canModifyBookingDiscounts(booking) && <button className="remove" onClick={ev => this.onRemoveDiscountClick(ev, d.discount)}>Remove</button> }
            <span className="value">{ d.amount ? '- ' + d.amount.format('$0.00') : '-' }</span>
          </div>) }
        </div> }
      { discounts !== null && discounts!.length === 0 && canModifyBookingDiscounts(booking) && isDraftBooking(booking) &&
        <div className="discounts">
          <h2>Discount</h2>
          <AddDiscountForm booking={booking as DraftBooking} onDiscountsChanged={this.onDiscountsChanged} />
        </div>
      }
      <div className="price">
        <div className="price">
          <span className="label">Total cost</span>
          <span className="value">{ price ? price!.format('$0.00') + ' VAT Incl.' : '-' }</span>
          { finalPaymentDate &&
            <span className="add-ons-note">Cost of add-ons to be debited on { finalPaymentDate!.format("MM/DD/YYYY") }</span> }
        </div>
        { this.props.priceChildren }
      </div>
    </div>;
  }
}